import { useState, useEffect, useCallback } from 'react'
import { BookOpen, Brain, Clock, Flame, Trophy, ChevronDown, ChevronRight, Star, RefreshCw, FileText, Search, Shuffle, Copy, Check } from 'lucide-react'
import confetti from 'canvas-confetti'
import { apiRequest } from '@api/client'
import type { LearnerStats, LearningTopic, TopicStatus, ClusterProgress } from './types'

function fireConfetti() {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#f59e0b', '#eab308', '#fbbf24', '#fcd34d', '#fef08a'],
  })
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<TopicStatus, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: 'text-gray-400', bg: 'bg-gray-500/10' },
  learning: { label: 'Learning', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  review: { label: 'Review', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  mastered: { label: 'Mastered', color: 'text-green-400', bg: 'bg-green-500/10' },
}

function ratingStars(rating: number | null | undefined) {
  if (!rating) return null
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={10}
          className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}
        />
      ))}
    </span>
  )
}

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return `${Math.floor(days / 30)}mo ago`
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({ icon: Icon, label, value, sublabel, color }: {
  icon: typeof BookOpen
  label: string
  value: string | number
  sublabel?: string
  color: string
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-lg font-bold text-[var(--text-primary)]">{value}</div>
        <div className="text-xs text-[var(--text-muted)]">{label}</div>
        {sublabel && <div className="text-xs text-[var(--text-muted)] opacity-70">{sublabel}</div>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Topic Row (expandable to show prompt)
// ---------------------------------------------------------------------------

function TopicRow({ topic: t, learnerId, onStatusChange }: {
  topic: LearningTopic
  learnerId: number
  onStatusChange: () => void
}) {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)

  const status = (t.status as TopicStatus) || 'new'
  const cfg = STATUS_CONFIG[status]

  const togglePrompt = async () => {
    if (open) { setOpen(false); return }
    setOpen(true)
    if (prompt !== null) return
    setLoading(true)
    try {
      const res = await apiRequest<{ success: boolean; data: LearningTopic }>(`/learning/topics/${t.id}`)
      if (res.success && res.data?.data?.prompt) setPrompt(res.data.data.prompt)
      else setPrompt('No prompt available.')
    } catch { setPrompt('Failed to load prompt.') }
    finally { setLoading(false) }
  }

  const handleStatusChange = async (newStatus: TopicStatus) => {
    if (newStatus === status) return
    setUpdating(true)
    try {
      await apiRequest(`/learning/learners/${learnerId}/progress/${t.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      })
      if (newStatus === 'learning') fireConfetti()
      onStatusChange()
    } catch (e) {
      console.error('Failed to update status:', e)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3 px-3 py-2 pl-10">
        <select
          value={status}
          onChange={(e) => handleStatusChange(e.target.value as TopicStatus)}
          disabled={updating}
          className={`text-xs pl-1.5 pr-4 py-0.5 rounded ${cfg.bg} ${cfg.color} font-medium shrink-0 border border-transparent hover:border-[var(--border-color)] outline-none cursor-pointer`}
          title="Change status"
        >
          {(['new', 'learning', 'review', 'mastered'] as TopicStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_CONFIG[s].label}
            </option>
          ))}
        </select>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[var(--text-primary)] truncate">{t.title}</div>
          {t.memory_hook && (
            <div className="text-xs text-[var(--text-muted)] truncate mt-0.5">{t.memory_hook}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ratingStars(t.last_rating)}
          {t.last_session_at && (
            <span className="text-xs text-[var(--text-muted)]">{timeAgo(t.last_session_at)}</span>
          )}
          {t.next_review_at && status !== 'mastered' && new Date(t.next_review_at) <= new Date() && (
            <span className="text-xs text-amber-400 font-medium">Due</span>
          )}
          <button
            onClick={togglePrompt}
            className="p-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors"
            title="View prompt"
          >
            <FileText size={12} className={open ? 'text-[var(--accent-color)]' : 'text-[var(--text-muted)]'} />
          </button>
        </div>
      </div>
      {open && (
        <div className="mx-10 mb-2 p-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <RefreshCw size={10} className="animate-spin" /> Loading prompt...
            </div>
          ) : (
            <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-sans leading-relaxed">{prompt}</pre>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Cluster Row
// ---------------------------------------------------------------------------

function ClusterRow({ cluster, topics, expanded, onToggle, learnerId, onStatusChange }: {
  cluster: ClusterProgress & { topics?: LearningTopic[] }
  topics: LearningTopic[]
  expanded: boolean
  onToggle: () => void
  learnerId: number
  onStatusChange: () => void
}) {
  const pct = cluster.total > 0 ? Math.round((cluster.mastered / cluster.total) * 100) : 0
  const inProgressPct = cluster.total > 0 ? Math.round((cluster.in_progress / cluster.total) * 100) : 0

  return (
    <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-[var(--bg-tertiary)] transition-colors text-left"
      >
        {expanded ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[var(--text-primary)] truncate">{cluster.cluster}</div>
          <div className="flex items-center gap-2 mt-1">
            {/* Progress bar */}
            <div className="flex-1 h-1.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
              <div className="h-full flex">
                <div className="bg-green-500 transition-all" style={{ width: `${pct}%` }} />
                <div className="bg-blue-500/60 transition-all" style={{ width: `${inProgressPct}%` }} />
              </div>
            </div>
            <span className="text-xs text-[var(--text-muted)] shrink-0 w-16 text-right">
              {cluster.mastered}/{cluster.total}
            </span>
          </div>
        </div>
        {cluster.avg_rating != null && (
          <div className="text-xs text-[var(--text-muted)] shrink-0">
            avg {cluster.avg_rating}
          </div>
        )}
      </button>

      {expanded && (
        <div className="border-t border-[var(--border-color)] divide-y divide-[var(--border-color)]">
          {topics.map((t) => (
            <TopicRow key={t.id} topic={t} learnerId={learnerId} onStatusChange={onStatusChange} />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function LearningPlanTool() {
  const [stats, setStats] = useState<LearnerStats | null>(null)
  const [topics, setTopics] = useState<LearningTopic[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<TopicStatus | 'all' | 'due'>('all')
  const [search, setSearch] = useState('')
  const [suggestedTopic, setSuggestedTopic] = useState<LearningTopic | null>(null)
  const [copied, setCopied] = useState(false)

  const LEARNER_ID = 1 // Alitia

  const loadData = useCallback(async () => {
    try {
      const [statsRes, topicsRes] = await Promise.all([
        apiRequest<{ success: boolean; data: LearnerStats }>(`/learning/learners/${LEARNER_ID}/stats`),
        apiRequest<{ success: boolean; data: { topics: LearningTopic[]; clusters: string[] } }>(`/learning/topics?learner_id=${LEARNER_ID}`),
      ])
      if (statsRes.success && statsRes.data?.data) setStats(statsRes.data.data)
      if (topicsRes.success && topicsRes.data?.data) setTopics(topicsRes.data.data.topics)
    } catch (e) {
      console.error('Failed to load learning data:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const [suggestLoading, setSuggestLoading] = useState(false)
  const [suggestedPrompt, setSuggestedPrompt] = useState<string | null>(null)

  const suggestRandomTopic = async () => {
    const newTopics = topics.filter((t) => !t.status || t.status === 'new')
    if (newTopics.length === 0) { setSuggestedTopic(null); return }
    const pick = newTopics[Math.floor(Math.random() * newTopics.length)]
    setSuggestedTopic(pick)
    setSuggestedPrompt(null)
    setCopied(false)
    setSuggestLoading(true)
    try {
      const res = await apiRequest<{ success: boolean; data: LearningTopic }>(`/learning/topics/${pick.id}`)
      if (res.success && res.data?.data?.prompt) setSuggestedPrompt(res.data.data.prompt)
      else setSuggestedPrompt(null)
    } catch { setSuggestedPrompt(null) }
    finally { setSuggestLoading(false) }
  }

  const buildClaudePrompt = (topic: LearningTopic, fullPrompt: string | null) => {
    const header = `Teach me about "${topic.title}" (from the "${topic.cluster}" cluster).`
    if (fullPrompt) {
      return `${header}\n\nHere is the teaching prompt to follow:\n\n---\n${fullPrompt}\n---\n\nRun an interactive teaching session based on the prompt above. Ask me questions, explain concepts, and test my understanding. Keep it conversational and engaging.`
    }
    return `${header}\n\nRun an interactive teaching session on this topic. Ask me questions, explain concepts, and test my understanding. Keep it conversational and engaging.`
  }

  const copyPrompt = async (topic: LearningTopic) => {
    navigator.clipboard.writeText(buildClaudePrompt(topic, suggestedPrompt))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    // Auto-mark as learning
    const status = (topic.status as TopicStatus) || 'new'
    if (status === 'new') {
      try {
        await apiRequest(`/learning/learners/${LEARNER_ID}/progress/${topic.id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'learning' }),
        })
        fireConfetti()
        loadData()
      } catch { /* silent */ }
    }
  }

  const toggleCluster = (cluster: string) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev)
      if (next.has(cluster)) next.delete(cluster)
      else next.add(cluster)
      return next
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={20} className="animate-spin text-[var(--text-muted)]" />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center py-20 text-[var(--text-muted)]">
        Failed to load learning data. Check that migrations have been applied.
      </div>
    )
  }

  const { counts, dueForReview, streak, clusterProgress, recentSessions, totalTopics } = stats
  const completionPct = totalTopics > 0 ? Math.round((counts.mastered / totalTopics) * 100) : 0

  // Filter topics
  const searchLower = search.toLowerCase()
  const filteredTopics = topics.filter((t) => {
    const status = (t.status as TopicStatus) || 'new'
    if (filter === 'due') {
      if (!(t.next_review_at && new Date(t.next_review_at) <= new Date() && status !== 'mastered')) return false
    } else if (filter !== 'all') {
      if (status !== filter) return false
    }
    if (search && !t.title.toLowerCase().includes(searchLower) && !t.cluster.toLowerCase().includes(searchLower)) return false
    return true
  })

  // Group filtered topics by cluster
  const topicsByCluster = new Map<string, LearningTopic[]>()
  for (const t of filteredTopics) {
    const list = topicsByCluster.get(t.cluster) || []
    list.push(t)
    topicsByCluster.set(t.cluster, list)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Learning Plan</h2>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {stats.learner.name}'s identity SME training — {totalTopics} topics over {stats.learner.target_months} months
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={suggestRandomTopic}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
            title="Suggest a random topic to learn"
          >
            <Shuffle size={14} className="text-amber-400" />
            <span className="text-xs font-medium text-amber-400">Random Topic</span>
          </button>
          <button
            onClick={() => { setLoading(true); loadData() }}
            className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} className="text-[var(--text-muted)]" />
          </button>
        </div>
      </div>

      {/* Suggested Topic */}
      {suggestedTopic && (
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Shuffle size={14} className="text-amber-400 shrink-0" />
                <span className="text-sm font-medium text-[var(--text-primary)]">Suggested Topic</span>
                <span className="text-xs text-[var(--text-muted)]">{suggestedTopic.cluster}</span>
              </div>
              <div className="text-sm text-[var(--text-primary)] mb-2">{suggestedTopic.title}</div>
              <div className="p-2.5 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] max-h-48 overflow-y-auto">
                {suggestLoading ? (
                  <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                    <RefreshCw size={10} className="animate-spin" /> Loading prompt...
                  </div>
                ) : (
                  <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-sans leading-relaxed">{buildClaudePrompt(suggestedTopic, suggestedPrompt)}</pre>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => copyPrompt(suggestedTopic)}
                disabled={suggestLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--accent-color)] text-white text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy Prompt'}
              </button>
              <button
                onClick={suggestRandomTopic}
                className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                title="Try another"
              >
                <Shuffle size={14} className="text-[var(--text-muted)]" />
              </button>
              <button
                onClick={() => setSuggestedTopic(null)}
                className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-[var(--text-muted)] text-xs"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Trophy} label="Mastered" value={counts.mastered} sublabel={`${completionPct}% complete`} color="bg-green-500/10 text-green-400" />
        <StatCard icon={Brain} label="In Progress" value={counts.learning + counts.review} color="bg-blue-500/10 text-blue-400" />
        <StatCard icon={BookOpen} label="Not Started" value={counts.new} color="bg-gray-500/10 text-gray-400" />
        <StatCard icon={Clock} label="Due for Review" value={dueForReview} sublabel={dueForReview > 0 ? 'Topics need revisiting' : 'All caught up'} color="bg-amber-500/10 text-amber-400" />
        <StatCard icon={Flame} label="Day Streak" value={streak} sublabel={streak > 0 ? 'Keep it going!' : 'Start today'} color="bg-red-500/10 text-red-400" />
      </div>

      {/* Overall Progress Bar */}
      <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[var(--text-primary)]">Overall Progress</span>
          <span className="text-sm text-[var(--text-muted)]">{counts.mastered + counts.learning + counts.review} / {totalTopics} started ({counts.mastered} mastered)</span>
        </div>
        <div className="h-3 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
          <div className="h-full flex">
            <div className="bg-green-500 transition-all duration-500" style={{ width: `${(counts.mastered / totalTopics) * 100}%` }} />
            <div className="bg-blue-500/60 transition-all duration-500" style={{ width: `${((counts.learning + counts.review) / totalTopics) * 100}%` }} />
          </div>
        </div>
        <div className="flex gap-4 mt-2">
          <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Mastered
          </span>
          <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <span className="w-2 h-2 rounded-full bg-blue-500/60" /> In Progress
          </span>
          <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
            <span className="w-2 h-2 rounded-full bg-[var(--bg-tertiary)]" /> Not Started
          </span>
        </div>
      </div>

      {/* Recent Sessions */}
      {recentSessions.length > 0 && (
        <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">Recent Sessions</h3>
          <div className="space-y-2">
            {recentSessions.slice(0, 5).map((s) => (
              <div key={s.topic_id} className="flex items-center gap-3">
                <span className="text-xs text-[var(--text-muted)] shrink-0 w-16">{timeAgo(s.last_session_at)}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-[var(--text-primary)] truncate block">{s.title}</span>
                </div>
                {ratingStars(s.last_rating)}
                {s.session_summary && (
                  <span className="text-xs text-[var(--text-muted)] truncate max-w-[200px]">{s.session_summary}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search topics..."
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)]"
        />
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--border-color)] pb-0">
        {([
          { key: 'all', label: `All (${totalTopics})` },
          { key: 'due', label: `Due (${dueForReview})` },
          { key: 'new', label: `New (${counts.new})` },
          { key: 'learning', label: `Learning (${counts.learning})` },
          { key: 'review', label: `Review (${counts.review})` },
          { key: 'mastered', label: `Mastered (${counts.mastered})` },
        ] as Array<{ key: typeof filter; label: string }>).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              filter === tab.key
                ? 'border-[var(--accent-color)] text-[var(--accent-color)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Cluster List */}
      <div className="space-y-2">
        {clusterProgress.filter((cp) => topicsByCluster.has(cp.cluster)).map((cp) => (
          <ClusterRow
            key={cp.cluster}
            cluster={cp}
            topics={topicsByCluster.get(cp.cluster) || []}
            expanded={expandedClusters.has(cp.cluster)}
            onToggle={() => toggleCluster(cp.cluster)}
            learnerId={LEARNER_ID}
            onStatusChange={loadData}
          />
        ))}
        {topicsByCluster.size === 0 && (
          <div className="text-center py-8 text-[var(--text-muted)] text-sm">
            No topics match this filter.
          </div>
        )}
      </div>
    </div>
  )
}
